import { FirstCleanCard } from '../components/quote/first-clean-card.tsx';
import '../components/work-page.css';
import './quote-editor.css';
import { DustEvidenceCard } from '../components/quote/dust-evidence-card.tsx';
import { WalkthroughReviewCard } from '../components/quote/walkthrough-review-card.tsx';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, paymentApi, singleJobApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { useQuoteEditor } from '../hooks/use-quote-editor.ts';
import { useDocumentTitle, useUnsavedChangesWarning } from '../hooks/index.ts';
import { Alert, Button, LoadingBlock, Select, StatusBadge, TextInput } from '../components/ui.tsx';
import { quoteBuilderSteps, QuoteStepper, type QuoteStepId } from '../components/quote-steps.tsx';
import { HowOften } from '../components/quote/how-often.tsx';
import { PlainPrice } from '../components/quote/plain-price.tsx';
import { headlineReconciliation, PriceBreakdown } from '../components/quote/price-breakdown.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { SaveIndicator } from '../components/quote/save-indicator.tsx';
import { CloneQuoteButton } from '../components/quote/clone-button.tsx';
import { QuoteActionsOverflow } from '../components/quote/actions-overflow.tsx';
import { ScheduleQuoteButton } from '../components/quote/schedule-button.tsx';
import { TotalsPanel } from '../components/quote/totals-panel.tsx';
import { QuoteProfitLine } from '../components/quote/quote-profit.tsx';
import RoomsStep from '../components/quote/rooms-step.tsx';
import { formatMinutes } from '../i18n/format.ts';
import { formatShortDate } from '../i18n/datetime.ts';
import PricingStep, { type TierDiscountNote } from '../components/quote/pricing-step.tsx';
import QuoteMarginPanel from '../components/quote/quote-margin-panel.tsx';
import ReviewStep from '../components/quote/review-step.tsx';
import Attachments from '../components/quote/attachments.tsx';
import AddressLink from '../components/address-link.tsx';
import { QuotePayments, quotePaymentsKey } from '../components/payments/quote-payments.tsx';
import { SingleJobCard, singleJobKey } from '../components/quote/single-job-card.tsx';
import { usePlanFeature } from '../components/plan-gate.tsx';
import { isOneTimeQuote } from '@shared/quotes/payments.ts';
import {
  CLOSED_QUOTE_STATUSES,
  LOCKED_QUOTE_STATUSES,
  isScheduledQuote,
  OPEN_APPOINTMENT_STATUSES,
  type QuoteStatus,
  type SeriesFrequency,
} from '@shared/constants/index.ts';

export default function QuoteEditorPage() {
  const { t } = useTranslation();
  const { quoteId = '' } = useParams();
  const navigate = useNavigate();
  const { company, can, currentLocation, locations, switchLocation } = useSession();

  const editor = useQuoteEditor(quoteId);
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * `?location=`: a link to this quote from a list that spans locations (the
   * dashboard's all-locations owed card), opened in a new tab or window. A
   * quote opens only in its own location, so the session moves there first,
   * as the schedule's deep links do, and the param goes once it has, so a
   * refresh does not repeat it. Only to a location this person already has;
   * one that cannot be switched to drops the param, and the page says what
   * it can.
   */
  const deepLinkLocationId = searchParams.get('location');
  const switchingForLink = Boolean(
    deepLinkLocationId &&
    deepLinkLocationId !== currentLocation?.id &&
    typeof switchLocation === 'function' &&
    (locations ?? []).some((entry) => entry.id === deepLinkLocationId),
  );
  const switchAsked = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkLocationId) return;
    const dropParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('location');
      setSearchParams(next, { replace: true });
    };
    if (!switchingForLink) {
      dropParam();
      return;
    }
    if (switchAsked.current === deepLinkLocationId) return;
    switchAsked.current = deepLinkLocationId;
    void switchLocation(deepLinkLocationId).catch(dropParam);
  }, [deepLinkLocationId, switchingForLink, switchLocation, searchParams, setSearchParams]);
  // Book again lands on the copy's review, the short way to check it before
  // booking: everything is still editable from the steps above.
  const [step, setStep] = useState<QuoteStepId>(() => (searchParams.get('bookAgain') ? 'send' : 'start'));
  useEffect(() => {
    if (searchParams.get('bookAgain')) setStep('send');
  }, [quoteId, searchParams]);
  // What this session knows about a frequency's discount that the quote cannot
  // say for itself (see `tierDiscountNotes` in pricing-step). Held here rather
  // than in the pricing step, which unmounts whenever the estimator looks at
  // another step — but keyed to the quote, because this page is a route and
  // React Router hands the same instance the next `/quotes/:quoteId` rather
  // than mounting a new one: notes about one quote must not outlive it.
  const tierDiscountNotes = useMemo(() => new Map<SeriesFrequency, TierDiscountNote>(), [quoteId]);

  const quote = editor.quote;
  useDocumentTitle(
    quote
      ? t('quoteEditor.documentTitleWithNumber', { number: quote.quoteNumber })
      : t('quoteEditor.documentTitleFallback'),
  );
  useUnsavedChangesWarning(editor.isDirty);

  // Save when the user moves between steps (spec §11.1).
  async function goToStep(next: QuoteStepId) {
    await editor.save();
    setStep(next);
  }

  const customer = useQuery({
    queryKey: ['customer', quote?.customerId],
    queryFn: () => customerApi.get(quote!.customerId),
    enabled: Boolean(quote?.customerId),
  });

  const properties = useQuery({
    queryKey: ['customer-properties', quote?.customerId],
    queryFn: () => customerApi.properties(quote!.customerId),
    enabled: Boolean(quote?.customerId),
  });

  // One job at a time (`single_job`): on a plan with no calendar, an approved
  // quote — or a Book again copy on its way to being booked — is a job with a
  // card of its own, which takes over finishing the work and taking its
  // payment. So is a booked quote being revised: editing it reopens it as a
  // draft while its visit stays booked, and this card is the only place on
  // the plan to move or cancel that visit.
  const hasScheduling = usePlanFeature('scheduling');
  const hasSingleJob = usePlanFeature('single_job');
  // So is a canceled job still holding money, whose card is where it is
  // refunded: canceling ended its schedule, so nothing on a revision of its
  // quote says it was ever booked. The quote's ledger knows, and the Payments
  // card below reads the same one.
  const ledger = useQuery({
    queryKey: quotePaymentsKey(quoteId),
    queryFn: () => paymentApi.quotePayments(quoteId),
    enabled: Boolean(quote) && !hasScheduling && hasSingleJob && can('payments:read'),
  });
  const showJobCard =
    Boolean(quote) &&
    !hasScheduling &&
    hasSingleJob &&
    (quote?.status === 'accepted' ||
      quote?.status === 'completed' ||
      Boolean(quote?.sourceQuoteId) ||
      (quote?.scheduledSeriesCount ?? 0) > 0 ||
      ledger.data?.jobHoldsPayment === true);
  const job = useQuery({
    queryKey: singleJobKey(quoteId),
    queryFn: () => singleJobApi.get(quoteId),
    enabled: showJobCard,
  });
  const jobBooked = Boolean(job.data?.visit && job.data.visit.status !== 'cancelled');
  // Still to happen: its customer and address are the visit's, and it is
  // canceled on its card, which closes the visit and its payment link too.
  const jobOpen = Boolean(job.data?.visit && OPEN_APPOINTMENT_STATUSES.includes(job.data.visit.status));

  // What the job offers (Book it, and whether a copy needs the customer's
  // approval) is worked out from the quote as saved, so every save that
  // changes it asks for the job again.
  const queryClient = useQueryClient();
  const savedVersion = editor.serverQuote?.version ?? null;
  const jobSeenAt = useRef<{ quoteId: string; version: number | null } | null>(null);
  useEffect(() => {
    const seen = jobSeenAt.current;
    jobSeenAt.current = { quoteId, version: savedVersion };
    if (!seen || seen.quoteId !== quoteId || seen.version === null || seen.version === savedVersion) return;
    void queryClient.invalidateQueries({ queryKey: singleJobKey(quoteId) });
  }, [queryClient, quoteId, savedVersion]);

  if (editor.isLoading || switchingForLink) return <LoadingBlock label={t('quoteEditor.loading')} />;

  if (editor.loadError || !quote) {
    return (
      <Alert tone="error" title={t('quoteEditor.loadErrorTitle')}>
        <p>{editor.loadError?.message ?? t('quoteEditor.mayHaveBeenDeleted')}</p>
        <Link to="/quotes" className="mt-2 inline-block text-[color:var(--color-brand)] hover:underline">
          {t('quoteEditor.backToAllQuotes')}
        </Link>
      </Alert>
    );
  }

  const status = quote.status as QuoteStatus;
  // Declined and canceled quotes are history; accepted ones stay editable but
  // editing them creates a revision (spec §22).
  const readOnly = !can('quotes:write') || (CLOSED_QUOTE_STATUSES.includes(status) && status !== 'completed');
  // The quote's own snapshot, so an old quote keeps reading in the currency
  // it was priced in even after the company setting changes.
  const currency = quote.currencyCode ?? company?.currencyCode ?? DEFAULT_CURRENCY_CODE;

  // How often comes first, for a home or a business. The rest of the quote
  // is the same three steps.
  const steps = quoteBuilderSteps();
  const stepIndex = steps.findIndex((candidate) => candidate.id === step);
  const previousStep = steps[stepIndex - 1];
  const nextStep = steps[stepIndex + 1];
  const reconciliation = headlineReconciliation(quote, editor.preview);
  const stickyAmount = (cents: number) => {
    const money = formatCents(cents, currency, getDateLocale());
    return editor.preview.billingBasis === 'monthly' ? t('totalsPanel.perMonthAmount', { amount: money }) : money;
  };

  return (
    <div className="work-page quote-editor-frame">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold">{quote.quoteNumber}</h1>
            <StatusBadge status={status} expired={quote.isExpired} quote={quote} />
            {/* Which kind of work this is — fixed at creation, so a plain
                badge rather than a control. Residential shows nothing: it is
                the default everywhere and a label would just be noise. */}
            {quote.segment === 'commercial' ? (
              <span className="badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
                {t('quoteEditor.commercialBadge')}
              </span>
            ) : null}
            {quote.revisionNumber > 1 ? (
              <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                {t('quoteEditor.revision', { number: quote.revisionNumber })}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[color:var(--color-muted)]">
            {quote.customerNameSnapshot}
            {quote.propertyAddressSnapshot ? ` · ${quote.propertyAddressSnapshot.split('\n')[0]}` : ''}
          </p>
        </div>
        <div className="text-right">
          <SaveIndicator state={editor.saveState} lastSavedAt={editor.lastSavedAt} />
          {isScheduledQuote(quote) || LOCKED_QUOTE_STATUSES.includes(status) ? (
            <ScheduleQuoteButton quote={quote} prepare={editor.save} />
          ) : null}
          {!readOnly ? (
            <QuoteActionsOverflow label={t('quotes.list.moreActions')}>
              {!(isScheduledQuote(quote) || LOCKED_QUOTE_STATUSES.includes(status)) ? (
                <ScheduleQuoteButton quote={quote} prepare={editor.save} />
              ) : null}
              {!readOnly ? <CloneQuoteButton quote={quote} prepare={editor.save} /> : null}
            </QuoteActionsOverflow>
          ) : null}
          <Button variant="ghost" onClick={() => navigate('/quotes')}>
            {t('quoteEditor.backToAllQuotes')}
          </Button>
        </div>
      </div>

      {editor.saveState === 'conflict' ? (
        <div className="mb-4">
          <Alert tone="error" title={t('quoteEditor.conflictTitle')}>
            <p>{t('quoteEditor.conflictMessage')}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={() => void editor.reloadFromServer()}>{t('quoteEditor.reloadLatest')}</Button>
            </div>
          </Alert>
        </div>
      ) : null}

      {editor.saveState === 'error' && editor.saveError ? (
        <div className="mb-4">
          <Alert tone="error" title={t('quoteEditor.saveErrorTitle')}>
            <p>{editor.saveError}</p>
            <Button className="mt-2" onClick={() => void editor.save()}>
              {t('quoteEditor.tryAgain')}
            </Button>
          </Alert>
        </div>
      ) : null}

      {!editor.isOnline ? (
        <div className="mb-4">
          <Alert tone="warning" title={t('quoteEditor.offlineTitle')}>
            {t('quoteEditor.offlineMessage')}
          </Alert>
        </div>
      ) : null}

      {readOnly ? (
        <div className="mb-4">
          <Alert tone="info">
            {can('quotes:write') ? (
              <>
                <p>{t('quoteEditor.readOnlyWithWrite', { status: t(`quotes.status.${status}`).toLowerCase() })}</p>
                {/*
                  The banner has always recommended this; without a control the
                  advice was a dead end, since the only route to the endpoint
                  was the API itself. The same control as the quote list's, so
                  a copy made from a closed quote can also be addressed to
                  somebody else — which is most of why anyone copies one.
                */}
                <CloneQuoteButton
                  quote={quote}
                  variant="primary"
                  className="mt-3"
                  label={t('quoteEditor.duplicateThisQuote')}
                />
              </>
            ) : (
              t('quoteEditor.readOnlyNoWrite')
            )}
          </Alert>
        </div>
      ) : null}

      {/* What the customer has paid on it, and the way to record the next
          payment — above the steps, because getting paid is not a step of
          building the quote and an accepted quote is mostly visited for it. */}
      {/* Both keyed by the quote: going from one quote straight to another
          reuses this page, and a half-filled form must not carry over to it.
          The keys differ between the two, since siblings sharing one key are
          not told apart: the job card was left on the page twice. */}
      {showJobCard ? <SingleJobCard key={`job-${quote.id}`} quote={quote} prepare={editor.save} /> : null}
      {/* Until the job has loaded, nobody knows it is not booked: the quote's own
          Record payment waits rather than offering a second way to pay a job. */}
      <QuotePayments
        key={`payments-${quote.id}`}
        quote={quote}
        recordedOnJob={showJobCard && (!job.data || jobBooked)}
      />

      <QuoteStepper steps={steps} current={step} onSelect={(next) => void goToStep(next)} />

      <div
        className={`quote-editor-body grid grid-cols-1 gap-6 ${step === 'send' ? '' : reconciliation ? 'pb-56 lg:pb-0' : 'pb-28 lg:pb-0'}`}
      >
        <div>
          {step === 'start' ? (
            <>
              <HowOften quote={quote} update={editor.update} readOnly={readOnly} />
              {/* Who and where, in one card, as the phone shows it: the dates
                  are rarely touched, so they wait behind their own line. */}
              <section className="card" aria-labelledby="customer-step-heading">
                <h2 id="customer-step-heading" className="mb-4 text-lg font-semibold">
                  {t('quoteEditor.customerHeading')}
                </h2>
                {customer.data ? (
                  <div className="mb-4">
                    <p className="font-semibold">{customer.data.displayName}</p>
                    <p className="text-[color:var(--color-muted)]">
                      {[customer.data.email, customer.data.phone].filter(Boolean).join(' · ') ||
                        t('quoteEditor.noContactDetails')}
                    </p>
                    <Link
                      to={`/customers/${customer.data.id}`}
                      className="mt-2 inline-block text-[color:var(--color-brand)] hover:underline"
                    >
                      {t('quoteEditor.editCustomerDetails')}
                    </Link>
                  </div>
                ) : (
                  <LoadingBlock label={t('quoteEditor.loading')} />
                )}
                <Select
                  label={t('quoteEditor.serviceAddress')}
                  hint={jobOpen ? t('singleJob.addressBooked') : jobBooked ? t('singleJob.addressDone') : undefined}
                  value={quote.propertyId ?? ''}
                  disabled={readOnly || jobBooked}
                  onChange={(event) => editor.update((draft) => ({ ...draft, propertyId: event.target.value || null }))}
                  options={[
                    { value: '', label: t('quoteEditor.notSpecified') },
                    ...(properties.data?.items ?? []).map((property) => ({
                      value: property.id,
                      label:
                        property.name ??
                        [property.addressLine1, property.city].filter(Boolean).join(', ') ??
                        t('quoteEditor.propertyFallbackLabel'),
                    })),
                  ]}
                />
                {quote.propertyAddressSnapshot ? (
                  <p className="whitespace-pre-line text-[color:var(--color-muted)]">
                    <AddressLink address={quote.propertyAddressSnapshot}>{quote.propertyAddressSnapshot}</AddressLink>
                  </p>
                ) : null}
                <Link
                  to={`/customers/${quote.customerId}`}
                  className="mt-4 inline-block text-[color:var(--color-brand)] hover:underline"
                >
                  {t('quoteEditor.addOrEditProperties')}
                </Link>
                <details className="mt-4 border-t border-[color:var(--color-line)] pt-4">
                  <summary className="min-h-11 cursor-pointer font-semibold">
                    {t('quoteEditor.datesHeading')}{' '}
                    <span className="font-normal text-[color:var(--color-muted)]">
                      {quote.expirationDate
                        ? t('quoteEditor.datesSummary', {
                            quoteDate: formatShortDate(quote.quoteDate),
                            validUntil: formatShortDate(quote.expirationDate),
                          })
                        : formatShortDate(quote.quoteDate)}
                    </span>
                  </summary>
                  <div className="mt-3 grid gap-x-4 sm:grid-cols-2">
                    <TextInput
                      label={t('quoteEditor.quoteDate')}
                      type="date"
                      value={quote.quoteDate}
                      disabled={readOnly}
                      onChange={(event) => editor.update((draft) => ({ ...draft, quoteDate: event.target.value }))}
                    />
                    <TextInput
                      label={t('quoteEditor.validUntil')}
                      type="date"
                      hint={t('quoteEditor.validUntilHint')}
                      value={quote.expirationDate ?? ''}
                      disabled={readOnly}
                      onChange={(event) =>
                        editor.update((draft) => ({ ...draft, expirationDate: event.target.value || null }))
                      }
                    />
                  </div>
                </details>
              </section>
            </>
          ) : null}

          {step === 'work' ? (
            <RoomsStep
              quote={quote}
              update={editor.update}
              readOnly={readOnly}
              onOpenTasks={() => {}}
              canSaveLayout={can('templates:write')}
              {...(company ? { companyCurrencyCode: company.currencyCode } : {})}
            />
          ) : null}

          {step === 'send' ? (
            <>
              {/* The side panel already says the price once the page is wide
                  enough to show it; below that, it is said here. */}
              <section className="quote-editor-inline-price card mb-6" aria-labelledby="plain-price-heading">
                <h2 id="plain-price-heading" className="mb-2 text-lg font-semibold">
                  {t('quoteBuilder.priceHeading')}
                </h2>
                <PlainPrice quote={quote} previewTotalCents={editor.preview.totalCents} currencyCode={currency} />
              </section>
              {/* Everything that changes the price or holds it back, folded into
                  one line as the phone keeps it behind Pricing. It opens on its
                  own whenever one of the checks is stopping the quote. */}
              <PricingAndChecks
                attention={Boolean(
                  quote.pricingBlocked || quote.walkthroughStatus?.pricingBlocked || quote.dustStatus?.blocked,
                )}
              >
                <details className="card mb-6">
                  <summary className="cursor-pointer text-lg font-semibold">{t('quoteBuilder.advanced')}</summary>
                  <p className="mb-4 mt-1 text-sm text-[color:var(--color-muted)]">{t('quoteBuilder.advancedHint')}</p>
                  <PricingStep
                    quote={quote}
                    update={editor.update}
                    preview={editor.preview}
                    currencyCode={currency}
                    readOnly={readOnly}
                    canOverrideTotal={can('quotes:price_override')}
                    tierDiscountNotes={tierDiscountNotes}
                  />
                </details>
                <QuoteMarginPanel
                  quote={quote}
                  serverQuote={editor.serverQuote}
                  currencyCode={currency}
                  readOnly={readOnly}
                  onApplied={(server) => editor.applyServerQuote(server, editor.savedEditCount())}
                />
                <FirstCleanCard key={`first-clean:${quote.id}`} editor={editor} readOnly={readOnly} />
                <DustEvidenceCard
                  key={`dust:${editor.quote?.id}:${editor.quote?.propertyId}`}
                  editor={editor}
                  readOnly={readOnly}
                />
                <WalkthroughReviewCard
                  key={`${quote.id}:${quote.customerId}:${quote.propertyId}:${quote.walkthrough?.assessmentRevision}:${quote.walkthrough?.policy.revision}`}
                  editor={editor}
                  readOnly={readOnly}
                />
              </PricingAndChecks>
              <ReviewStep
                quote={quote}
                update={editor.update}
                currencyCode={currency}
                readOnly={readOnly}
                save={editor.save}
                applyServerQuote={editor.applyServerQuote}
                // Only for the frequency prices: the tier figures the customer
                // reads are the Worker's, so the step has to know which of them
                // the draft has since edited the discount out from under.
                serverQuote={editor.serverQuote}
                savedEditCount={editor.savedEditCount}
                suspendAutosave={editor.suspendAutosave}
                // Already loaded for the customer card above; the review step
                // needs it for the one fact a quote does not snapshot — whether
                // this customer has agreed to be texted.
                customer={customer.data ?? null}
                // Attachments belong to the quote rather than a step, and are
                // shown alongside the review so they are reachable without
                // leaving it. The step places them, above its own Finish.
                attachments={<Attachments quoteId={quoteId} readOnly={readOnly} />}
                // The job says whether it is one: a booked job stays the
                // one-time visit it was booked as while a revision changes
                // how often the work repeats.
                completedOnJob={
                  showJobCard &&
                  (job.data
                    ? job.data.oneTime
                    : isOneTimeQuote({
                        acceptedFrequency: quote.acceptedFrequency,
                        preferredFrequency: quote.preferredFrequency ?? null,
                        billingBasis: quote.billingBasis,
                      }))
                }
                cancelOnJob={showJobCard && jobOpen}
              />
            </>
          ) : null}

          {/* Only the steps that exist get a button. At either end of the wizard
              the missing direction is left out rather than rendered disabled: a
              grayed-out "Next" on the last step reads as broken instead of as
              "you are done". The empty div keeps "Next" right-aligned when
              "Back" is absent. */}
          <div className="mt-6 flex justify-between">
            {previousStep ? (
              <Button onClick={() => void goToStep(previousStep.id)}>{t('quoteEditor.back')}</Button>
            ) : (
              <div />
            )}
            {nextStep ? (
              <Button variant="primary" onClick={() => void goToStep(nextStep.id)}>
                {t('quoteEditor.next')}
              </Button>
            ) : null}
          </div>
        </div>

        <TotalsPanel
          quote={quote}
          preview={editor.preview}
          currencyCode={currency}
          profitLine={<QuoteProfitLine quote={quote} preview={editor.preview} currencyCode={currency} />}
        />
      </div>

      {step !== 'send' ? (
        // Above the phone's bottom navigation bar and beside the sidebar, where
        // there is one (index.css).
        <div className="fixed right-0 bottom-[var(--shell-bottom-nav,0px)] left-[var(--shell-sidebar,0px)] z-20 border-t border-[color:var(--color-line)] bg-white p-3 shadow lg:hidden">
          <PlainPrice quote={quote} previewTotalCents={editor.preview.totalCents} currencyCode={currency} />
          {reconciliation ? <PriceBreakdown lines={reconciliation} amount={stickyAmount} /> : null}
          <p className="text-sm font-semibold">
            {t('quoteWork.aboutTime', {
              time: formatMinutes(
                finiteMinutes(
                  quote.billingBasis === 'monthly'
                    ? editor.preview.estimatedMinutes
                    : editor.preview.routineVisitMinutes || editor.preview.estimatedMinutes,
                ),
              ),
            })}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The Send step's price adjustments and site checks, closed by default. It
 * opens itself when a check starts holding the quote back, and never closes
 * itself: a section that folded shut the moment a review was saved would take
 * the confirmation with it.
 */
function PricingAndChecks({ attention, children }: { attention: boolean; children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(attention);
  useEffect(() => {
    if (attention) setOpen(true);
  }, [attention]);
  return (
    <details className="mb-6" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="card mb-4 min-h-11 cursor-pointer">
        <span className="text-lg font-semibold">{t('quoteEditor.pricingChecksHeading')}</span>
        {attention ? (
          <span className="badge ml-3 border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
            {t('quoteEditor.needsAttention')}
          </span>
        ) : null}
        <span className="mt-1 block text-sm text-[color:var(--color-muted)]">{t('quoteEditor.pricingChecksHint')}</span>
      </summary>
      {children}
    </details>
  );
}

function finiteMinutes(minutes: number | undefined): number {
  return typeof minutes === 'number' && Number.isFinite(minutes) ? minutes : 0;
}
